import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type OnChangeFn,
  type Row,
  type RowData,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Shared data-table primitives.
 *
 * Visual contract (modeled on the affiliate-program detail roster, the
 * reference table for the whole app):
 *  - auto layout: `meta.width` is a hint, `meta.fit` sizes a column to its
 *    content, and anything wider than the container scrolls
 *  - h-10 rows, py-1.5 cells, text-sm
 *  - text-xs medium sentence-case muted headers on a border-b
 *  - divide-y divide-border body, hover:bg-muted/60 rows
 *  - first column pl-5, last column pr-5 (the table hugs its card)
 *  - skeleton rows that mirror the column layout while loading
 *  - a single full-width empty-state row
 *
 * Rows can be made clickable with `onRowClick`; clicks that originate on
 * interactive elements (buttons, links, inputs) inside a cell are ignored so
 * per-row actions keep working.
 *
 * Sorting: columns opt in with `enableSorting: true` in their def. With
 * `manualSorting` the table only reports state changes (server sorting);
 * otherwise TanStack sorts client-side.
 */

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Tailwind width class applied to the <col> (table is table-fixed). */
    width?: string;
    /**
     * Size the column to its longest value (emails, URLs, ids) instead of
     * sharing space: nothing in it is ever clipped, and a wide table scrolls
     * in its wrapper. Prefer this over a fixed `width` for content columns.
     */
    fit?: boolean;
    headerClassName?: string;
    cellClassName?: string;
    /** Custom skeleton cell content while loading. */
    skeleton?: React.ReactNode;
  }
}

const INTERACTIVE_SELECTOR =
  "button, a, input, select, textarea, label, [role='button'], [data-row-click-exempt]";

export interface DataTableProps<TData> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  data: TData[];
  isLoading?: boolean;
  /** Skeleton row count while loading (default 8). */
  skeletonRows?: number;
  emptyText?: React.ReactNode;
  /** Makes rows clickable (adds cursor + keyboard access). */
  onRowClick?: (row: TData) => void;
  rowClassName?: (row: TData) => string | undefined;
  getRowId?: (row: TData, index: number) => string;
  /** Controlled sorting state — pass both to enable sortable headers. */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /** True when the server sorts; TanStack then leaves row order alone. */
  manualSorting?: boolean;
  /** Controlled row selection (for checkbox columns). */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  className?: string;
  /**
   * Minimum table width (Tailwind class). Below this the wrapper scrolls
   * horizontally instead of crushing the fixed-percentage columns into each
   * other on small screens. When omitted, a floor is derived from the column
   * count (~7rem per column, at least 40rem). Pass e.g. "min-w-0" for
   * genuinely tiny tables.
   */
  minTableWidth?: string;
  /**
   * Max height (Tailwind class, e.g. "max-h-[28rem]"). When set, the wrapper
   * also scrolls vertically and the header row sticks to the top.
   */
  maxHeight?: string;
}

const FLEXIBLE_COLUMN_REM = 10;
/** A content-sized column (numbers, dates, an action cell) is narrow. */
const FIT_COLUMN_REM = 6;

/** Width in rem a column needs: its Tailwind `w-N` / `w-[Npx]` width, else the flexible default. */
function columnMinRem(widthClass: string | undefined, fit?: boolean): number {
  if (fit) return FIT_COLUMN_REM;
  if (!widthClass) return FLEXIBLE_COLUMN_REM;
  const scale = /(?:^|\s)w-(\d+(?:\.\d+)?)(?:\s|$)/.exec(widthClass);
  if (scale) return Math.max(Number(scale[1]) / 4, 1);
  const px = /(?:^|\s)w-\[(\d+(?:\.\d+)?)px\](?:\s|$)/.exec(widthClass);
  if (px) return Number(px[1]) / 16;
  const rem = /(?:^|\s)w-\[(\d+(?:\.\d+)?)rem\](?:\s|$)/.exec(widthClass);
  if (rem) return Number(rem[1]);
  return FLEXIBLE_COLUMN_REM;
}

export function DataTable<TData>({
  columns,
  data,
  isLoading,
  skeletonRows = 8,
  emptyText = "No results",
  onRowClick,
  rowClassName,
  getRowId,
  sorting,
  onSortingChange,
  manualSorting,
  rowSelection,
  onRowSelectionChange,
  className,
  minTableWidth,
  maxHeight,
}: DataTableProps<TData>) {
  const sortingEnabled = sorting !== undefined && onSortingChange !== undefined;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(sortingEnabled
      ? {
          getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
          manualSorting,
          onSortingChange,
        }
      : {}),
    ...(rowSelection !== undefined ? { onRowSelectionChange } : {}),
    state: {
      ...(sortingEnabled ? { sorting } : {}),
      ...(rowSelection !== undefined ? { rowSelection } : {}),
    },
    enableSorting: sortingEnabled,
    ...(getRowId ? { getRowId } : {}),
    defaultColumn: { enableSorting: false },
  });

  const leafColumns = table.getAllLeafColumns();
  const showSkeleton = Boolean(isLoading) && data.length === 0;

  // Horizontal floor so narrow containers scroll instead of crushing every
  // column: fixed-width columns (`meta.width` like `w-56`) count at their
  // declared width, everything else at ~10rem, 40rem minimum. Auto layout
  // still lets content-sized (`meta.fit`) columns grow past their share, so
  // long emails and URLs are never clipped. A minTableWidth class overrides.
  const derivedMinWidth = minTableWidth
    ? undefined
    : `max(40rem, ${leafColumns.reduce(
        (total, col) =>
          total + columnMinRem(col.columnDef.meta?.width, col.columnDef.meta?.fit),
        0,
      )}rem)`;

  const handleRowClick = (e: React.MouseEvent, row: Row<TData>) => {
    if (!onRowClick) return;
    const target = e.target as HTMLElement;
    if (target.closest(INTERACTIVE_SELECTOR)) return;
    onRowClick(row.original);
  };

  // Wheel handling, native because React's onWheel is passive and could not
  // cancel the page scroll:
  // - A table that scrolls vertically keeps the wheel to itself, even at its
  //   ends, so the page never lurches while the pointer is over it.
  // - A table that only overflows sideways scrolls sideways on a plain wheel.
  // - A table with nothing to scroll leaves the wheel to the page.
  const scrollRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.deltaY === 0) return;
      const tall = el.scrollHeight > el.clientHeight + 1;
      const wide = el.scrollWidth > el.clientWidth + 1;
      if (tall) {
        if (e.shiftKey || e.deltaX !== 0) return;
        const atEnd =
          e.deltaY > 0
            ? el.scrollTop + el.clientHeight >= el.scrollHeight - 1
            : el.scrollTop <= 0;
        if (atEnd) e.preventDefault();
        return;
      }
      if (!wide || e.shiftKey || e.deltaX !== 0) return;
      const atEdge =
        e.deltaY > 0 ? el.scrollLeft + el.clientWidth >= el.scrollWidth - 1 : el.scrollLeft <= 0;
      if (atEdge) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <div
      ref={scrollRef}
      className={cn(
        "overflow-x-auto",
        maxHeight && cn("overflow-y-auto", maxHeight),
        className,
      )}
    >
      <table
        className={cn("w-full table-auto text-sm", minTableWidth)}
        style={derivedMinWidth ? { minWidth: derivedMinWidth } : undefined}
      >
        <colgroup>
          {leafColumns.map((col) => (
            <col
              key={col.id}
              // `w-px` is the shrink-to-fit hint: with nowrap cells the
              // column settles at its widest value and no wider.
              className={
                col.columnDef.meta?.fit ? "w-px" : col.columnDef.meta?.width
              }
            />
          ))}
        </colgroup>
        <thead className="text-left text-xs text-muted-foreground">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="border-b border-border">
              {headerGroup.headers.map((header, index) => {
                const canSort = header.column.getCanSort();
                const sortDir = header.column.getIsSorted();
                return (
                  <th
                    key={header.id}
                    className={cn(
                      // px-3 is the gutter between columns; the outer edges
                      // keep the wider pl-5/pr-5 so the table aligns with its card.
                      // Headers stay on one line; a column that cannot fit
                      // its label widens (auto layout) rather than wrapping it.
                      "py-1.5 px-3 font-medium whitespace-nowrap",
                      index === 0 && "pl-5",
                      index === headerGroup.headers.length - 1 && "pr-5",
                      // Sticky per-<th> (not <thead>) for Safari; the inset
                      // shadow replaces the row border that scrolls away.
                      maxHeight &&
                        "sticky top-0 z-10 bg-card shadow-[inset_0_-1px_0_0_hsl(var(--border))]",
                      header.column.columnDef.meta?.headerClassName,
                    )}
                  >
                    {header.isPlaceholder ? null : canSort ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        <span className="inline-flex flex-col -space-y-1">
                          <ChevronUp
                            className={cn(
                              "h-2.5 w-2.5",
                              sortDir === "asc"
                                ? "text-foreground"
                                : "text-muted-foreground/40",
                            )}
                          />
                          <ChevronDown
                            className={cn(
                              "h-2.5 w-2.5",
                              sortDir === "desc"
                                ? "text-foreground"
                                : "text-muted-foreground/40",
                            )}
                          />
                        </span>
                      </button>
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className="divide-y divide-border">
          {showSkeleton ? (
            Array.from({ length: skeletonRows }).map((_, rowIndex) => (
              <tr key={`skeleton-${rowIndex}`} className="h-10">
                {leafColumns.map((col, colIndex) => (
                  <td
                    key={col.id}
                    className={cn(
                      "py-1.5 px-3",
                      colIndex === 0 && "pl-5",
                      colIndex === leafColumns.length - 1 && "pr-5",
                      col.columnDef.meta?.cellClassName,
                    )}
                  >
                    {col.columnDef.meta?.skeleton ?? (
                      <div className="h-3.5 w-3/4 max-w-32 rounded bg-muted animate-pulse" />
                    )}
                  </td>
                ))}
              </tr>
            ))
          ) : table.getRowModel().rows.length === 0 ? (
            <tr className="h-10">
              <td
                colSpan={leafColumns.length}
                className="py-10 text-center text-sm text-muted-foreground"
              >
                {emptyText}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={(e) => handleRowClick(e, row)}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === "Enter" && e.target === e.currentTarget) {
                          onRowClick(row.original);
                        }
                      }
                    : undefined
                }
                tabIndex={onRowClick ? 0 : undefined}
                className={cn(
                  "h-10 hover:bg-muted/60 transition-colors",
                  onRowClick &&
                    "cursor-pointer focus-visible:outline-none focus-visible:bg-muted/60",
                  rowClassName?.(row.original),
                )}
              >
                {row.getVisibleCells().map((cell, cellIndex) => (
                  <td
                    key={cell.id}
                    className={cn(
                      "py-1.5 px-3",
                      cell.column.columnDef.meta?.fit && "whitespace-nowrap",
                      cellIndex === 0 && "pl-5",
                      cellIndex === row.getVisibleCells().length - 1 && "pr-5",
                      cell.column.columnDef.meta?.cellClassName,
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const DEFAULT_PAGE_SIZE_OPTIONS = [20, 50, 100, 500];

export interface DataTablePaginationProps {
  page: number;
  totalPages: number;
  /** Total row count across pages. */
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  /** Plural noun after the count, e.g. "affiliates". */
  noun?: string;
  className?: string;
}

/** The reference footer: "Showing X–Y of Z" · rows-per-page · prev/next. */
export function DataTablePagination({
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  noun,
  className,
}: DataTablePaginationProps) {
  const safeTotalPages = Math.max(totalPages, 1);
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <div
      className={cn(
        "mt-auto flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3 text-sm",
        className,
      )}
    >
      <p className="text-muted-foreground">
        {total === 0 ? (
          <>
            Showing <span className="font-medium text-foreground">0</span>
          </>
        ) : (
          <>
            Showing{" "}
            <span className="font-medium text-foreground">
              {rangeStart}–{rangeEnd}
            </span>{" "}
            of <span className="font-medium text-foreground">{total}</span>
          </>
        )}
        {noun ? ` ${noun}` : null}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {onPageSizeChange && (
          <label className="flex items-center gap-2 text-muted-foreground">
            <span className="text-xs whitespace-nowrap">Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="rounded-lg border border-border bg-card px-2 py-1.5 text-sm text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/20"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <span className="px-2 text-muted-foreground whitespace-nowrap">
            Page {page} of {safeTotalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(safeTotalPages, page + 1))}
            disabled={page >= safeTotalPages}
            className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
